import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, InputNumber, message, Select } from 'antd';
import { useMutation, useQuery } from '@apollo/client';
import { TTBag } from '../graphql/mutations/createTTBag';
import {
    CREATE_TT_BAGS,
    CreateTTBagsInput,
    CreateTTBagsResponse,
    TTBagPresortSelectionInput,
    TTBagServiceSelectionInput,
} from '../graphql/mutations/createTTBags';
import { useCarrierServiceMap } from '../../../../hooks/useCarrierServiceMap';
import { useFinalMileCarrierOptions } from '../../sorterPlan/hooks/useFinalMileCarrierOptions';
import { useWarehouseContext } from '../../../../contexts/WarehouseContext';
import { useTranslation } from 'react-i18next';
import { GET_ALL_CARRIER_OPTIONS, GetAllCarrierOptionsResponse } from '../graphql/queries/getAllCarrierOptions';
import {
    GET_TT_LOCATION_OPTIONS,
    GetTtLocationOptionsResponse,
    TT_LOCATION_TYPE,
} from '../graphql/queries/getTtLocationOptions';
import {
    GET_TT_PRESORT_OPTIONS,
    GetTtPresortOptionsResponse,
} from '../graphql/queries/getTtPresortOptions';
import {
    PREVIEW_TT_BAG_RULES,
    PreviewTTBagRulesResponse,
    TTBagRulePreview,
} from '../graphql/queries/previewTTBagRules';
import {
    GET_DEFAULT_TT_FROM_CODE,
    GetDefaultTtFromCodeResponse,
} from '../graphql/queries/getDefaultTtFromCode';
import { GET_HUB_LIST, GetHubListResponse } from '../graphql/queries/getHubList';
import {
    GET_LM_LOCAL_HUB_SORTING_CODES,
    GetLmLocalHubSortingCodesResponse,
} from '../graphql/queries/getLmLocalHubSortingCodes';
import {
    GET_WAREHOUSE_LIST_BY_HUB_ID,
    GetWarehouseListByHubIdResponse,
} from '../graphql/queries/getWarehouseListByHubId';
import {
    GET_AREAS_BY_WAREHOUSE_ID,
    GetAreasByWarehouseIdResponse,
} from '../../../../graphql/queries/getAreasByWarehouseId';
import { Checkbox } from 'antd';
import { TT_BAG_FORM_CARRIER_ALL } from '../utils/carrierUtils';

/** SWX 本地派送 LM 尾程码：ALL 选项字面 */
const LM_ALL = '__ALL__';
/** LM 区块目的地 Hub 固定为本地支线发货仓：hubList 里 sortingCode 命中以下之一的那个（LA/LAX 两种口径都算，精确等值，不含 LAXS/LAXSTEMU 等） */
const LM_LOCAL_HUB_SORTING_CODES = ['LA', 'LAX'];
/** area.sortingCode 第三段拆字母(Region)/数字(Number)，拼成 Hub 前两段-Region-Number 四段码 */
const buildLmFourSegment = (areaSortingCode?: string): string | undefined => {
    if (!areaSortingCode) return undefined;
    const parts = areaSortingCode.split('-');
    if (parts.length < 3) return undefined;
    const firstTwo = parts.slice(0, 2).join('-');
    const letter = parts[2].match(/^[A-Za-z]+/)?.[0] || '';
    const digit = parts.length >= 4 ? parts[3] : parts[2].replace(/^[A-Za-z]+/, '');
    if (firstTwo && letter && digit) return `${firstTwo}-${letter}-${digit}`;
    return undefined;
};

const ALL_NEXT_STOP = 'ALL';
const ALL_CARRIER = TT_BAG_FORM_CARRIER_ALL;
const ALL_CHANNEL = 'ALL';
const ALL_PRESORT = '__ALL__';
const NO_PRESORT = '__NO_PRESORT__';
/** 尾程商服务多选中「不区分」选项字面（提交时映射为 includeUndifferentiated，后端退回枚举缩写做第三段） */
const UNDIFFERENTIATED = '__UNDIFFERENTIATED__';
/** 组合预览防抖间隔 */
const PREVIEW_DEBOUNCE_MS = 300;

export interface NewTTBagFormProps {
    visible: boolean;
    onClose: () => void;
    onSubmitSuccess: (bags: TTBag[], printCopies: number) => void;
}

/**
 * PreSort 多选（受控，供 Form.Item 注入 value/onChange）：ALL 与其它项互斥 —— 选中 ALL 即清空其它、
 * 且其它项置灰不可选（ALL 已含全部候选码 + 无预分拣），与 TT 业务类型 ALL 口径一致。
 */
const PresortMultiSelect: React.FC<{
    value?: string[];
    onChange?: (v: string[]) => void;
    options: { value: string; label: string }[];
    placeholder: string;
    disabled?: boolean;
}> = ({ value, onChange, options, placeholder, disabled }) => {
    const hasAll = (value ?? []).includes(ALL_PRESORT);
    return (
        <Select
            mode="multiple"
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            showSearch
            optionFilterProp="label"
            maxTagCount="responsive"
            options={options.map(o => ({ ...o, disabled: hasAll && o.value !== ALL_PRESORT }))}
            onChange={(vals: string[]) => onChange?.(vals.includes(ALL_PRESORT) ? [ALL_PRESORT] : vals)}
        />
    );
};

/** 单个尾程商的 PreSort 多选行（候选来自 tt_presort_config，按下一站过滤） */
const PresortSelect: React.FC<{
    carrierValue: number;
    carrierLabel: string;
    nextStops: string[];
    fromCode?: string;
    ttBizTypes: string[];
    visible: boolean;
    onOptionsLoaded: (carrierValue: number, codes: string[]) => void;
    disabled?: boolean;
}> = ({ carrierValue, carrierLabel, nextStops, fromCode, ttBizTypes, visible, onOptionsLoaded, disabled }) => {
    const { t } = useTranslation();
    const { data } = useQuery<GetTtPresortOptionsResponse>(GET_TT_PRESORT_OPTIONS, {
        variables: {
            carrierCode: carrierValue,
            nextStopCodes: nextStops.length > 0 ? nextStops : undefined,
            fromCode: fromCode || undefined,
            ttBizTypes: ttBizTypes.length > 0 ? ttBizTypes : undefined,
        },
        // 每次打开建包拉最新预分拣候选，反映期间在配置管理页的增改删（否则要整页刷新）
        fetchPolicy: 'cache-and-network',
        skip: !visible,
    });
    const codes = useMemo(
        () => Array.from(new Set((data?.getTtPresortOptions || []).map(o => o.presortCode))),
        [data]
    );
    useEffect(() => {
        // 切换下一站等触发重查时，加载中 data 为 undefined、codes 暂为空；此时不上报，
        // 避免用「加载中的空候选」误清掉尚有效的已选项。待查询返回再上报由父组件校正。
        if (!data) return;
        onOptionsLoaded(carrierValue, codes);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carrierValue, codes, data]);
    const codeOptions = useMemo(() => codes.map(code => ({ value: code, label: code })), [codes]);
    const options = [
        { value: ALL_PRESORT, label: 'ALL' },
        { value: NO_PRESORT, label: t('wms.bags.ttBag.noPresort') as string },
        ...codeOptions,
    ];
    return (
        <Form.Item
            style={{ marginBottom: 0 }}
            name={`presort_${carrierValue}`}
            label={t('wms.bags.ttBag.presortFor', { carrier: carrierLabel })}
            // 勾选 LM 尾程码时预分拣码互斥、禁用且清空，此时不校验必填
            rules={disabled ? [] : [{ required: true, type: 'array' as const, min: 1, message: t('wms.bags.ttBag.pleaseSelectPresort') as string }]}
            initialValue={[NO_PRESORT]}
        >
            <PresortMultiSelect
                options={options}
                placeholder={t('wms.bags.ttBag.pleaseSelectPresort') as string}
                disabled={disabled}
            />
        </Form.Item>
    );
};

/** 单个尾程商的「服务」多选行（候选来自 tt_location_config 子节点，服务 code 作分拣规则第三段） */
const ServiceSelect: React.FC<{
    carrierValue: number;
    carrierLabel: string;
    services: { code: string; name: string }[];
    /** 「不区分」选项的显示文案：用尾程商默认缩写（如 USM），无缩写时回退「不区分」 */
    undifferentiatedLabel: string;
}> = ({ carrierValue, carrierLabel, services, undifferentiatedLabel }) => {
    const { t } = useTranslation();
    const options = [
        { value: UNDIFFERENTIATED, label: undifferentiatedLabel },
        ...services.map(s => ({ value: s.code, label: s.code })),
    ];
    return (
        <Form.Item
            style={{ marginBottom: 0 }}
            name={`service_${carrierValue}`}
            label={t('wms.bags.ttBag.serviceFor', { carrier: carrierLabel })}
            rules={[{ required: true, type: 'array' as const, min: 1, message: t('wms.bags.ttBag.pleaseSelectService') as string }]}
            initialValue={[UNDIFFERENTIATED]}
        >
            <Select
                mode="multiple"
                placeholder={t('wms.bags.ttBag.pleaseSelectService') as string}
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                options={options}
            />
        </Form.Item>
    );
};

const NewTTBagForm: React.FC<NewTTBagFormProps> = ({ visible, onClose, onSubmitSuccess }) => {
    const { currentWarehouse } = useWarehouseContext();
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const submittingRef = useRef(false);

    // 尾程商服务映射：carrierCode(FinalMileCarrier.value) → 服务列表（tt_location_config 子节点）
    const { serviceMap: carrierServiceMap } = useCarrierServiceMap(visible);
    // 尾程商默认缩写（FinalMileCarrier.value → TT 三位缩写，如 USM）：用于「不区分」选项显示
    const { options: finalMileCarrierOptions } = useFinalMileCarrierOptions(true);
    const carrierAbbrMap = useMemo(() => {
        const m = new Map<number, string>();
        finalMileCarrierOptions.forEach(o => { if (o.abbreviation) m.set(o.value, o.abbreviation); });
        return m;
    }, [finalMileCarrierOptions]);

    const [ttChannel, setTtChannel] = useState<string[]>([]);
    const [ttNextStop, setTtNextStop] = useState<string[]>([]);
    const [ttCarrier, setTtCarrier] = useState<(number | string)[]>([]);
    // LM SortCode for SWX Local Delivery（SWX 本地派送尾程码，单选核心版；多选 ALL 联动后续增强）
    const [lmChecked, setLmChecked] = useState(false);
    const [lmStage, setLmStage] = useState<number>(4); // 阶段数 1/2/3/4，控制展开到 Hub/WHS/Region/Number 哪一级
    const [lmHubId, setLmHubId] = useState<string | undefined>(undefined);
    const [lmWhsId, setLmWhsId] = useState<string | undefined>(undefined);
    const [lmRegion, setLmRegion] = useState<string | undefined>(undefined);
    const [lmNumber, setLmNumber] = useState<string | undefined>(undefined);
    // 表单值变更计数器：驱动组合预览防抖刷新（含动态 PreSort 行）
    const [formVersion, setFormVersion] = useState(0);
    const [previewInput, setPreviewInput] = useState<CreateTTBagsInput | null>(null);
    // 各尾程商的预分拣候选码（PreSort 行加载后上报，供 ALL 展开）
    const [presortCodesByCarrier, setPresortCodesByCarrier] = useState<Record<number, string[]>>({});
    const handlePresortOptionsLoaded = (carrierValue: number, codes: string[]) => {
        setPresortCodesByCarrier(prev => {
            const prevCodes = prev[carrierValue] || [];
            if (prevCodes.length === codes.length && prevCodes.every((v, i) => v === codes[i])) return prev;
            return { ...prev, [carrierValue]: codes };
        });
        // 候选码变化（如切换下一站/业务类型）时，剔除已选但在新候选中已失效的预分拣码，保留 ALL/无预分拣，
        // 避免残留过期 tag（如切到 LAX1_A 后仍挂着上一个下一站的 DFW）。
        // setFieldsValue 不触发 onValuesChange，需手动 bump formVersion 驱动组合预览刷新。
        const field = `presort_${carrierValue}`;
        const current: string[] = form.getFieldValue(field) || [];
        const valid = current.filter(v => v === ALL_PRESORT || v === NO_PRESORT || codes.includes(v));
        if (valid.length !== current.length) {
            form.setFieldsValue({ [field]: valid });
            setFormVersion(v => v + 1);
        }
    };

    const { data: nextStopData, loading: nextStopLoading } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
        variables: { locationType: TT_LOCATION_TYPE.NEXT_STOP, warehouseId: currentWarehouse?.id },
        fetchPolicy: 'cache-and-network',
        skip: !visible,
    });

    const formatLocationLabel = (name: string, labelCode: string | null) =>
        labelCode ? `${name}_${labelCode}` : name;

    // 根据当前仓库过滤下一站候选项：LAXS 不显示 LAX2，LAXSCBT 不显示 LAX1
    const nextStopExcludeMap: Record<string, string> = { LAXS: 'LAX2', LAXSCBT: 'LAX1' };
    const excludedNextStop = currentWarehouse?.name ? nextStopExcludeMap[currentWarehouse.name] : undefined;
    const filteredNextStopOptions = useMemo(
        () => nextStopData?.getTtLocationOptions.filter(l => l.code !== excludedNextStop) || [],
        [nextStopData, excludedNextStop]
    );

    const toOptions = useMemo(
        () => filteredNextStopOptions.map(l => ({ value: l.code, label: formatLocationLabel(l.name, l.labelCode) })),
        [filteredNextStopOptions]
    );
    const nextStopOptionsBase = useMemo(
        () => [
            { value: ALL_NEXT_STOP, label: 'ALL' },
            ...filteredNextStopOptions.map(l => ({ value: l.code, label: formatLocationLabel(l.name, l.labelCode) })),
        ],
        [filteredNextStopOptions]
    );
    const nextStopOptions = useMemo(() => {
        const hasAll = ttNextStop.includes(ALL_NEXT_STOP);
        if (!hasAll) return nextStopOptionsBase;
        return nextStopOptionsBase.map(opt => ({ ...opt, disabled: opt.value !== ALL_NEXT_STOP }));
    }, [nextStopOptionsBase, ttNextStop]);

    const allNextStopCodes = useMemo(
        () => filteredNextStopOptions.map(l => l.code),
        [filteredNextStopOptions]
    );

    // TT 业务类型选项：来自 tt_location_config（location_type=6），code 与后端 TtBagBizType 枚举名一致
    const { data: bizTypeData, loading: bizTypeLoading } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
        variables: { locationType: TT_LOCATION_TYPE.TT_BIZ_TYPE },
        fetchPolicy: 'cache-and-network',
        skip: !visible,
    });
    const channelConfigOptions = useMemo(
        () => bizTypeData?.getTtLocationOptions.map(l => ({ value: l.code, label: l.name })) || [],
        [bizTypeData]
    );
    const channelLabel = (code: string) =>
        channelConfigOptions.find(o => o.value === code)?.label ?? code;
    /** ALL 展开的业务类型：含「不限」(UNLIMITED)——ALL 即全部业务类型 */
    const allBizTypes = useMemo(
        () => channelConfigOptions.map(o => o.value),
        [channelConfigOptions]
    );
    const channelOptionsBase = useMemo(
        () => [{ value: ALL_CHANNEL, label: 'ALL' }, ...channelConfigOptions],
        [channelConfigOptions]
    );
    const channelOptions = useMemo(() => {
        const hasAll = ttChannel.includes(ALL_CHANNEL);
        if (!hasAll) return channelOptionsBase;
        // ALL 覆盖全部业务类型（含不限），其余项一律置灰
        return channelOptionsBase.map(opt => ({
            ...opt,
            disabled: opt.value !== ALL_CHANNEL,
        }));
    }, [channelOptionsBase, ttChannel]);

    const warehouseIdNum = currentWarehouse?.id ? Number(currentWarehouse.id) : undefined;
    const { data: ttFromData, loading: ttFromLoading, error: ttFromError } = useQuery<GetDefaultTtFromCodeResponse>(
        GET_DEFAULT_TT_FROM_CODE,
        {
            variables: { warehouseId: warehouseIdNum ?? 0 },
            skip: !visible || warehouseIdNum == null || Number.isNaN(warehouseIdNum),
        }
    );
    const ttFromCode = ttFromData?.getDefaultTtFromCode ?? undefined;

    const { data: carrierData } = useQuery<GetAllCarrierOptionsResponse>(GET_ALL_CARRIER_OPTIONS, { skip: !visible });
    const { data: carrierLocationData, loading: carrierLoading } = useQuery<GetTtLocationOptionsResponse>(GET_TT_LOCATION_OPTIONS, {
        variables: { locationType: TT_LOCATION_TYPE.CARRIER },
        fetchPolicy: 'cache-and-network',
        skip: !visible,
    });
    // name → FinalMileCarrier.value 映射，用于创建包牌时传 carrierCode
    const carrierNameToValue = useMemo(() => {
        const map = new Map<string, number>();
        carrierData?.getAllCarrierOptions.forEach(c => map.set(c.name, c.value));
        return map;
    }, [carrierData]);
    const ttCarrierOptions = useMemo(
        () => (carrierLocationData?.getTtLocationOptions ?? [])
            // 仅保留顶级尾程商（parentId 为 null/0）；服务子节点（如 USMPM，parentId 指向尾程商）不应出现在尾程商下拉
            .filter(l => l.parentId == null || Number(l.parentId) === 0)
            .map(l => ({
                value: carrierNameToValue.get(l.code) ?? -1,
                label: l.labelCode ? `${l.name}_${l.labelCode}` : l.name
            })),
        [carrierLocationData, carrierNameToValue]
    );
    const ttCarrierOptionsBase = useMemo(() => [{ value: ALL_CARRIER, label: 'ALL' }, ...ttCarrierOptions], [ttCarrierOptions]);
    const ttCarrierOptionsWithAll = useMemo(() => {
        const hasAll = ttCarrier.includes(ALL_CARRIER);
        if (!hasAll) return ttCarrierOptionsBase;
        return ttCarrierOptionsBase.map(opt => ({
            ...opt,
            disabled: opt.value !== ALL_CARRIER,
        }));
    }, [ttCarrierOptionsBase, ttCarrier]);

    // MIX 值：MIX 包牌收多尾程商包裹，不参与预分拣候选，不渲染 PreSort 行
    const mixCarrierValue = useMemo(
        () => carrierData?.getAllCarrierOptions.find(c => c.name === 'MIX')?.value ?? -1,
        [carrierData]
    );
    const swxCarrierValue = useMemo(
        () => carrierData?.getAllCarrierOptions.find(c => c.name === 'SWIFTX')?.value ?? 0,
        [carrierData]
    );

    // ---------- LM SortCode 区块（SWX 本地派送尾程码）----------
    // 展示条件：下一站选中含当前仓 from（本地派送）且尾程商含 SWX
    const showLmBlock = useMemo(
        () => !!ttFromCode && ttNextStop.includes(ttFromCode) && ttCarrier.includes(swxCarrierValue),
        [ttFromCode, ttNextStop, ttCarrier, swxCarrierValue]
    );
    const { data: lmHubData } = useQuery<GetHubListResponse>(GET_HUB_LIST, { skip: !visible || !showLmBlock });
    // 本地派送目的 Hub 的 sortingCode 候选：按当前仓走 common_config 映射（后端 getLmLocalHubSortingCodes），
    // 未配置该仓时后端兜底 [LA, LAX]；查询未就绪时前端也用常量兜底
    const { data: lmHubCodeData } = useQuery<GetLmLocalHubSortingCodesResponse>(GET_LM_LOCAL_HUB_SORTING_CODES, {
        variables: { warehouseId: warehouseIdNum }, skip: !warehouseIdNum || !visible || !showLmBlock,
    });
    const lmLocalHubSortingCodes = lmHubCodeData?.getLmLocalHubSortingCodes ?? LM_LOCAL_HUB_SORTING_CODES;
    // 目的地 Hub 固定为本地支线发货仓（sortingCode 命中配置候选），置灰不可选
    const lmLocalHub = useMemo(
        () => lmHubData?.hubList?.find(h => !!h.sortingCode && lmLocalHubSortingCodes.includes(h.sortingCode)),
        [lmHubData, lmLocalHubSortingCodes]
    );
    const { data: lmWhsData } = useQuery<GetWarehouseListByHubIdResponse>(GET_WAREHOUSE_LIST_BY_HUB_ID, {
        variables: { hubId: lmHubId }, skip: !lmHubId || !showLmBlock,
    });
    const { data: lmAreaData } = useQuery<GetAreasByWarehouseIdResponse>(GET_AREAS_BY_WAREHOUSE_ID, {
        variables: { warehouseId: lmWhsId }, skip: !lmWhsId || !showLmBlock,
    });
    const lmRegionOptions = useMemo(() => {
        const set = new Set<string>();
        (lmAreaData?.areaList || []).forEach(a => {
            const letter = a.sortingCode?.split('-')[2]?.match(/^[A-Za-z]+/)?.[0];
            if (letter) set.add(letter);
        });
        return Array.from(set).map(r => ({ value: r, label: r }));
    }, [lmAreaData]);
    const lmNumberOptions = useMemo(() => {
        const opts = (lmAreaData?.areaList || [])
            .filter(a => a.sortingCode?.split('-')[2]?.match(/^[A-Za-z]+/)?.[0] === lmRegion)
            .map(a => {
                const seg3 = a.sortingCode?.split('-') || [];
                const digit = seg3.length >= 4 ? seg3[3] : (seg3[2]?.replace(/^[A-Za-z]+/, '') || '');
                return digit;
            })
            .filter(Boolean);
        return [{ value: LM_ALL, label: 'ALL' }, ...Array.from(new Set(opts)).map(n => ({ value: n, label: n }))];
    }, [lmAreaData, lmRegion]);
    // 按阶段数产出尾程码（对齐 SWX 建包 generateBagSortingCode）：
    // 1=Hub 码；2=WHS 码；3=前两段+区字母；4=四段码（需选到具体格口，Number=ALL/未选则退 TT 三段=undefined）
    const lmSortingCode = useMemo(() => {
        if (!showLmBlock || !lmChecked || !lmLocalHub) return undefined;
        if (lmStage <= 1) return lmLocalHub.sortingCode || undefined;
        if (!lmWhsId) return undefined;
        const whs = lmWhsData?.warehouseList?.find(w => w.id === lmWhsId);
        if (lmStage === 2) return whs?.sortingCode || undefined;
        if (!lmRegion) return undefined;
        if (lmStage === 3) {
            const area = (lmAreaData?.areaList || []).find(
                a => a.sortingCode?.split('-')[2]?.match(/^[A-Za-z]+/)?.[0] === lmRegion,
            );
            const parts = area?.sortingCode?.split('-') || [];
            if (parts.length < 3) return undefined;
            const firstTwo = parts.slice(0, 2).join('-');
            const letter = parts[2].match(/^[A-Za-z]+/)?.[0] || '';
            return firstTwo && letter ? `${firstTwo}-${letter}` : undefined;
        }
        if (!lmNumber || lmNumber === LM_ALL) return undefined;
        const area = (lmAreaData?.areaList || []).find(a => {
            const parts = a.sortingCode?.split('-') || [];
            const letter = parts[2]?.match(/^[A-Za-z]+/)?.[0];
            const digit = parts.length >= 4 ? parts[3] : (parts[2]?.replace(/^[A-Za-z]+/, '') || '');
            return letter === lmRegion && digit === lmNumber;
        });
        return buildLmFourSegment(area?.sortingCode);
    }, [showLmBlock, lmChecked, lmLocalHub, lmStage, lmWhsId, lmRegion, lmNumber, lmWhsData, lmAreaData]);

    // 展示条件不再满足时收起并清空 LM 选择
    useEffect(() => {
        if (!showLmBlock) {
            setLmChecked(false); setLmStage(4); setLmHubId(undefined); setLmWhsId(undefined); setLmRegion(undefined); setLmNumber(undefined);
        }
    }, [showLmBlock]);
    // 勾选后目的地 Hub 自动锁定为本地 Hub（LA）
    useEffect(() => {
        if (showLmBlock && lmChecked && lmLocalHub && lmHubId !== lmLocalHub.id) {
            setLmHubId(lmLocalHub.id);
        }
    }, [showLmBlock, lmChecked, lmLocalHub, lmHubId]);

    // ---------- 维度展开（ALL → 具体值） ----------
    // 业务类型 ALL 展开为全部业务类型（含不限 UNLIMITED）
    const expandChannels = (vals: string[]): string[] =>
        vals.includes(ALL_CHANNEL)
            ? Array.from(new Set([...allBizTypes, ...vals.filter(v => v !== ALL_CHANNEL)]))
            : vals;
    const expandNextStops = (vals: string[]): string[] =>
        vals.includes(ALL_NEXT_STOP) ? allNextStopCodes : vals;
    const expandCarriers = (vals: (number | string)[]): number[] =>
        vals.includes(ALL_CARRIER)
            ? ttCarrierOptions.map(o => o.value)
            : vals.filter((v): v is number => typeof v === 'number');

    const expandedCarriers = useMemo(() => expandCarriers(ttCarrier),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ttCarrier, ttCarrierOptions]);
    const expandedNextStops = useMemo(() => expandNextStops(ttNextStop),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ttNextStop, allNextStopCodes]);
    /** 已选业务类型（ALL 展开为具体类型，不含不限）；用于 PreSort 候选按业务类型过滤（后端再叠加不限兜底） */
    const expandedChannels = useMemo(() => expandChannels(ttChannel),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ttChannel, allBizTypes]);
    /** 渲染 PreSort 行的尾程商（排除 MIX） */
    const presortCarriers = useMemo(
        () => ttCarrierOptions.filter(o => expandedCarriers.includes(o.value) && o.value !== mixCarrierValue),
        [ttCarrierOptions, expandedCarriers, mixCarrierValue]
    );
    /** 渲染「尾程商服务」行的尾程商（在 tt_location_config 下有服务子节点的才渲染） */
    const serviceCarriers = useMemo(
        () => ttCarrierOptions.filter(o => expandedCarriers.includes(o.value) && (carrierServiceMap.get(o.value)?.length ?? 0) > 0),
        [ttCarrierOptions, expandedCarriers, carrierServiceMap]
    );

    /** 由当前表单值构建批量建包入参；维度不完整时返回 null */
    const buildInput = (bagQuantityPerRule: number): CreateTTBagsInput | null => {
        const channels = expandChannels(ttChannel);
        const nextStops = expandedNextStops;
        const carriers = expandedCarriers;
        if (channels.length === 0 || nextStops.length === 0 || carriers.length === 0) return null;
        const values = form.getFieldsValue();
        const presortSelections: TTBagPresortSelectionInput[] = presortCarriers.map(c => {
            const selected: string[] = values[`presort_${c.value}`] || [];
            const includeAll = selected.includes(ALL_PRESORT);
            return {
                carrierCode: c.value,
                // ALL 展开为该尾程商的全部候选码（后端语义：码存在且被选中才带码建包）
                presortCodes: includeAll
                    ? presortCodesByCarrier[c.value] || []
                    : selected.filter(v => v !== NO_PRESORT && v !== ALL_PRESORT),
                // ALL 含「无预分拣」：选 ALL 时同时建无码包（等同显式勾选「无预分拣」）
                includeNoPresort: selected.includes(NO_PRESORT) || includeAll,
            };
        });
        // 尾程商服务多选 → serviceSelections：仅含有服务子节点的尾程商；无服务的不传（后端退回枚举缩写）
        const serviceSelections: TTBagServiceSelectionInput[] = serviceCarriers
            .filter(c => carriers.includes(c.value))
            .map(c => {
                const selected: string[] = values[`service_${c.value}`] || [];
                return {
                    carrierCode: c.value,
                    serviceCodes: selected.filter(v => v !== UNDIFFERENTIATED),
                    includeUndifferentiated: selected.includes(UNDIFFERENTIATED),
                };
            })
            .filter(s => s.serviceCodes.length > 0 || s.includeUndifferentiated);
        return {
            warehouseId: currentWarehouse?.id!,
            ttBizTypes: channels,
            nextStops,
            carrierCodes: carriers,
            presortSelections,
            serviceSelections: serviceSelections.length > 0 ? serviceSelections : undefined,
            to: values.ttTo || undefined,
            bagQuantityPerRule,
            lmSortingCode: lmSortingCode || undefined,
        };
    };

    // ---------- 组合预览（防抖调用后端，与创建同一展开实现） ----------
    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => {
            const input = buildInput(1);
            setPreviewInput(input);
        }, PREVIEW_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, formVersion, ttChannel, ttNextStop, ttCarrier, presortCarriers, serviceCarriers, lmSortingCode]);

    const { data: previewData } = useQuery<PreviewTTBagRulesResponse>(PREVIEW_TT_BAG_RULES, {
        variables: { input: previewInput },
        skip: !visible || previewInput == null,
        fetchPolicy: 'cache-and-network',
    });
    const preview = previewData?.previewTTBagRules;

    const formatRuleLine = (r: TTBagRulePreview): string =>
        // 与包牌列表 computePromptSortingCode 口径一致：有预分拣码时 first_second（下划线拼接）
        r.sortingRuleSecond ? `${r.sortingRuleFirst}_${r.sortingRuleSecond}` : r.sortingRuleFirst;
    const previewGroups = useMemo(() => {
        if (!preview?.rules) return [];
        const orderedCodes = channelConfigOptions.map(o => o.value);
        // 配置未返回的 code 兜底追加，保证预览不吞组
        const codesInRules = Array.from(new Set(preview.rules.map(r => r.ttBizType)));
        const codes = [...orderedCodes, ...codesInRules.filter(c => !orderedCodes.includes(c))];
        return codes
            .map(code => ({
                code,
                lines: preview.rules.filter(r => r.ttBizType === code).map(formatRuleLine),
            }))
            .filter(g => g.lines.length > 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview, channelConfigOptions]);

    const resetTTState = () => {
        setTtChannel([]);
        setTtNextStop([]);
        setTtCarrier([]);
        setPreviewInput(null);
    };

    const handleExclusiveAllChange = <T,>(allValue: T, val: T[], fieldName: string, setter: (v: T[]) => void) => {
        if (val.includes(allValue)) {
            form.setFieldsValue({ [fieldName]: [allValue] });
            setter([allValue]);
        } else {
            setter(val);
        }
    };

    useEffect(() => {
        if (visible) {
            form.resetFields();
            resetTTState();
        }
    }, [visible, form]);

    const [createTTBags, { loading: creatingTT }] = useMutation<CreateTTBagsResponse>(CREATE_TT_BAGS);

    const handleSubmit = async () => {
        if (submittingRef.current) return;
        if (ttFromLoading) {
            message.warning(t('wms.bags.ttBag.loadingDefaultFrom'));
            return;
        }
        if (ttFromError) {
            message.error((ttFromError as Error)?.message || t('wms.bags.messages.failedToCreateTags'));
            return;
        }
        if (ttFromCode == null || ttFromCode === '') {
            message.error(t('wms.bags.ttBag.noDefaultFromMapping'));
            return;
        }
        submittingRef.current = true;
        try {
            const values = await form.validateFields();
            const input = buildInput(values.bagQuantity || 1);
            if (!input) {
                message.error(t('wms.bags.messages.failedToCreateTags'));
                return;
            }
            const result = await createTTBags({ variables: { input } });
            if (result.data?.result.errorMessage) {
                message.error(result.data.result.errorMessage);
                return;
            }
            const createdBags = result.data?.result.bags || [];
            if (createdBags.length > 0) {
                message.success(t('wms.bags.messages.successfullyCreatedTags', { count: createdBags.length }));
                onSubmitSuccess(createdBags, values.printCopies || 1);
                onClose();
            }
        } catch (error) {
            console.error('Form submission failed:', error);
            message.error(t('wms.bags.messages.failedToCreateTags'));
        } finally {
            submittingRef.current = false;
        }
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            onValuesChange={() => setFormVersion(v => v + 1)}
            initialValues={{
                bagQuantity: 1,
                printCopies: 1,
                ttBizTypes: [],
                ttNextStop: [],
                carrierCode: [],
            }}
        >
            <Form.Item
                name="ttBizTypes"
                label={t('wms.bags.ttBag.bizType')}
                rules={[{ required: true, type: 'array' as const, min: 1, message: t('wms.bags.ttBag.pleaseSelectBizType') as string }]}
            >
                <Select
                    mode="multiple"
                    placeholder={t('wms.bags.ttBag.pleaseSelectBizType') as string}
                    options={channelOptions}
                    loading={bizTypeLoading}
                    onChange={(val: string[]) => {
                        // 选 ALL 即全部业务类型（含不限），清掉其余项
                        if (val.includes(ALL_CHANNEL)) {
                            form.setFieldsValue({ ttBizTypes: [ALL_CHANNEL] });
                            setTtChannel([ALL_CHANNEL]);
                        } else {
                            setTtChannel(val);
                        }
                    }}
                    maxTagCount="responsive"
                />
            </Form.Item>

            <Form.Item
                name="ttNextStop"
                label={t('wms.bags.ttBag.nextStop')}
                rules={[{ required: true, type: 'array' as const, min: 1, message: t('wms.bags.ttBag.pleaseSelectNextStop') as string }]}
            >
                <Select
                    mode="multiple"
                    placeholder={t('wms.bags.ttBag.pleaseSelectNextStop') as string}
                    options={nextStopOptions}
                    loading={nextStopLoading}
                    showSearch
                    optionFilterProp="label"
                    onChange={(val: string[]) => handleExclusiveAllChange(ALL_NEXT_STOP, val, 'ttNextStop', setTtNextStop)}
                    maxTagCount="responsive"
                />
            </Form.Item>

            <Form.Item name="ttTo" label={t('wms.bags.ttBag.to')}>
                <Select
                    placeholder={t('wms.bags.ttBag.pleaseSelectTo') as string}
                    options={toOptions}
                    loading={nextStopLoading}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                />
            </Form.Item>

            <Form.Item
                name="carrierCode"
                label={t('wms.bags.pageDetails.carrier')}
                rules={[{ required: true, type: 'array' as const, min: 1, message: t('wms.bags.messages.pleaseSelectCarrier') || '' }]}
            >
                <Select
                    mode="multiple"
                    placeholder={t('wms.bags.placeholders.carrier')}
                    options={ttCarrierOptionsWithAll}
                    loading={carrierLoading}
                    showSearch
                    optionFilterProp="label"
                    onChange={(val: (number | string)[]) => handleExclusiveAllChange<number | string>(ALL_CARRIER, val, 'carrierCode', setTtCarrier)}
                    maxTagCount="responsive"
                />
            </Form.Item>

            {presortCarriers.length > 0 && (
                <Form.Item label=" " colon={false} className="my-2">
                    <div className="border border-gray-100 rounded px-3 py-2 bg-gray-100/60 space-y-2">
                        {presortCarriers.map(c => (
                            <PresortSelect
                                key={c.value}
                                carrierValue={c.value}
                                carrierLabel={c.label}
                                nextStops={expandedNextStops}
                                fromCode={ttFromCode}
                                ttBizTypes={expandedChannels}
                                visible={visible}
                                onOptionsLoaded={handlePresortOptionsLoaded}
                                disabled={lmChecked}
                            />
                        ))}
                    </div>
                </Form.Item>
            )}

            {serviceCarriers.length > 0 && (
                <Form.Item label=" " colon={false} className="my-2">
                    <div className="border border-gray-100 rounded px-3 py-2 bg-gray-100/60 space-y-2">
                        {serviceCarriers.map(c => (
                            <ServiceSelect
                                key={c.value}
                                carrierValue={c.value}
                                carrierLabel={c.label}
                                services={carrierServiceMap.get(c.value) ?? []}
                                undifferentiatedLabel={carrierAbbrMap.get(c.value) ?? (t('wms.bags.ttBag.undifferentiated') as string)}
                            />
                        ))}
                    </div>
                </Form.Item>
            )}

            {showLmBlock && (
                <Form.Item label=" " colon={false} className="my-4">
                    <div className="border border-gray-100 rounded px-3 py-3 bg-gray-100/60">
                        <Checkbox
                            checked={lmChecked}
                            onChange={e => {
                                const checked = e.target.checked;
                                setLmChecked(checked);
                                // 勾选 LM 尾程码 → 与预分拣码互斥：清空所有已选预分拣码
                                if (checked) {
                                    const cleared: Record<string, string[]> = {};
                                    presortCarriers.forEach(c => { cleared[`presort_${c.value}`] = []; });
                                    if (Object.keys(cleared).length > 0) {
                                        form.setFieldsValue(cleared);
                                        setFormVersion(v => v + 1);
                                    }
                                }
                            }}
                        >
                            {t('wms.bags.ttBag.useLmSortCode')}
                        </Checkbox>
                        {lmChecked && (
                            <div className="mt-3 grid grid-cols-[120px_1fr] gap-y-3 gap-x-2 items-center">
                                <span className="text-gray-600">{t('wms.bags.ttBag.stageNumber')}</span>
                                <Select
                                    value={lmStage}
                                    options={[1, 2, 3, 4].map(n => ({ value: n, label: String(n) }))}
                                    onChange={(v: number) => {
                                        setLmStage(v);
                                        if (v < 2) setLmWhsId(undefined);
                                        if (v < 3) setLmRegion(undefined);
                                        if (v < 4) setLmNumber(undefined);
                                    }}
                                />
                                <span className="text-gray-600">{t('wms.bags.ttBag.destinationHub')}</span>
                                <Select
                                    placeholder={t('wms.bags.ttBag.destinationHub') as string}
                                    value={lmLocalHub?.id}
                                    disabled
                                    options={lmLocalHub ? [{ value: lmLocalHub.id, label: lmLocalHub.name }] : []}
                                />
                                {lmStage >= 2 && (
                                    <>
                                        <span className="text-gray-600">{t('wms.bags.ttBag.destinationWhs')}</span>
                                        <Select
                                            placeholder={t('wms.bags.ttBag.destinationWhs') as string}
                                            value={lmWhsId}
                                            options={lmWhsData?.warehouseList?.map(w => ({ value: w.id, label: w.name }))}
                                            onChange={(v?: string) => { setLmWhsId(v); setLmRegion(undefined); setLmNumber(undefined); }}
                                            disabled={!lmHubId}
                                            showSearch optionFilterProp="label"
                                        />
                                    </>
                                )}
                                {lmStage >= 3 && (
                                    <>
                                        <span className="text-gray-600">{t('wms.bags.ttBag.region')}</span>
                                        <Select
                                            placeholder={t('wms.bags.ttBag.region') as string}
                                            value={lmRegion}
                                            options={lmRegionOptions}
                                            onChange={(v?: string) => { setLmRegion(v); setLmNumber(undefined); }}
                                            disabled={!lmWhsId}
                                            showSearch optionFilterProp="label"
                                        />
                                    </>
                                )}
                                {lmStage >= 4 && (
                                    <>
                                        <span className="text-gray-600">{t('wms.bags.ttBag.number')}</span>
                                        <Select
                                            placeholder={t('wms.bags.ttBag.number') as string}
                                            value={lmNumber}
                                            options={lmNumberOptions}
                                            onChange={(v?: string) => setLmNumber(v)}
                                            disabled={!lmRegion}
                                        />
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </Form.Item>
            )}

            {(previewGroups.length > 0 || preview?.errorMessage) && (
                <Form.Item label={t('wms.bags.ttBag.sortingRule')}>
                    <div style={{ wordBreak: 'break-all' }}>
                        {previewGroups.map(g => (
                            <div key={g.code}>
                                {channelLabel(g.code)}: {g.lines.slice(0, 3).join(', ')}
                                {g.lines.length > 3 ? ' ...' : ''}
                            </div>
                        ))}
                        {preview && !preview.errorMessage && (
                            <div>{t('wms.bags.ttBag.ruleTotalCount', { count: preview.totalCount })}</div>
                        )}
                        {preview?.errorMessage && (
                            <span className="text-red-500">{preview.errorMessage}</span>
                        )}
                    </div>
                </Form.Item>
            )}

            <Form.Item
                name="bagQuantity"
                label={t('wms.bags.ttBag.bagQuantityPerRule')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseEnterBagQuantity') || '' }]}
            >
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
                name="printCopies"
                label={t('wms.bags.pageDetails.printCopies')}
                rules={[{ required: true, message: t('wms.bags.messages.pleaseEnterPrintCopies') || '' }]}
            >
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <div className="flex justify-end gap-2">
                <Button onClick={onClose}>{t('components.button.cancel')}</Button>
                <Button
                    type="primary"
                    loading={creatingTT || ttFromLoading}
                    disabled={!!ttFromError || ttFromCode == null || ttFromCode === '' || !!preview?.errorMessage}
                    onClick={handleSubmit}
                >
                    {t('components.button.create')}
                </Button>
            </div>
        </Form>
    );
};

export default NewTTBagForm;
